import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CalendarDays, CalendarX2, ChevronLeft, ChevronRight, Clock, Globe2,
  User, Users, Video, AlertTriangle, LayoutGrid, Rows3, List, Film, Link as LinkIcon, Download, BookOpen, FileText } from "lucide-react";
import { toast } from "sonner";
import { toISODate, todayISO as crTodayISO, hora12 } from "@/lib/dateFormat";
import { LIVE_TYPES as TYPES, liveStyle } from "@/lib/liveTypes";
import LiveSessionCard from "@/components/LiveSessionCard";
import AddToCalendarMenu from "@/components/AddToCalendarMenu";
import CalendarSyncCard from "@/components/CalendarSyncCard";

const iso = (d) => toISODate(d);
// "Hoy" en Costa Rica, no el reloj del navegador.
const crToday = () => new Date(`${crTodayISO()}T12:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd); };

export default function LiveClasses() {
  const { t, lang } = useI18n();
  const isMobile = useIsMobile();
  const locale = lang === "en" ? "en-US" : "es-CR";
  const [tab, setTab] = useState("explore");
  // Todos los eventos de una vez es la vista principal; semana y mes quedan
  // como calendario y el filtro de semanas acota la lista.
  const [view, setView] = useState("all");
  const [weekFilter, setWeekFilter] = useState("all");
  const [anchor, setAnchor] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [mine, setMine] = useState([]);
  const [myLevel, setMyLevel] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const range = useMemo(() => {
    if (view === "all") return { from: new Date(), to: null };
    if (view === "week") {
      const from = mondayOf(anchor);
      return { from, to: addDays(from, 6) };
    }
    const from = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const to = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from, to };
  }, [anchor, view]);

  const load = async () => {
    setLoading(true);
    const params = { date_from: iso(range.from) };
    if (range.to) params.date_to = iso(range.to);
    // En el calendario (semana/mes) las pasadas se ven en gris; en la lista no.
    if (view !== "all") params.include_past = true;
    if (typeFilter !== "all") params.activity_type = typeFilter;
    if (levelFilter !== "all") params.level = levelFilter;
    const [a, b] = await Promise.all([
      api.get("/live-classes/available", { params }),
      api.get("/live-classes/mine"),
    ]);
    setSessions(a.data.sessions);
    setMyLevel(a.data.my_level);
    setMine(b.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [typeFilter, levelFilter, view, anchor]);

  // Contador de clases que quedan en el mes (baja al terminar cada sesión)
  const [monthLeft, setMonthLeft] = useState(null);
  useEffect(() => {
    api.get("/live-sessions/month-count").then((r) => setMonthLeft(r.data)).catch(() => {});
  }, [sessions]);

  useEffect(() => { if (isMobile && view === "month") setView("week"); }, [isMobile, view]);

  const refresh = async () => { await load(); setDetail(null); };

  const book = async (id) => {
    try {
      const r = await api.post(`/live-classes/${id}/book`);
      toast.success(r.data.status === "booked" ? t("live_booked_ok") : t("live_waitlisted_ok"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const unbook = async (id) => {
    try {
      await api.delete(`/live-classes/${id}/book`);
      toast.success(t("live_unbooked_ok"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const rangeLabel = useMemo(() => {
    if (view === "all" || !range.to) return t("live_view_all");
    if (view === "month") {
      return range.from.toLocaleDateString(locale, { month: "long", year: "numeric" });
    }
    const f = range.from.toLocaleDateString(locale, { day: "numeric", month: "short" });
    const to = range.to.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
    return `${f} – ${to}`;
  }, [range, view, locale, t]);

  const list = useMemo(() => {
    const base = tab === "explore" ? sessions : mine;
    if (view !== "all" || weekFilter === "all") return base;
    return base.filter((s) => iso(mondayOf(new Date(`${s.date}T12:00:00`))) === weekFilter);
  }, [tab, sessions, mine, view, weekFilter]);

  // Semanas disponibles según las sesiones cargadas (para el filtro)
  const weekOptions = useMemo(() => {
    const map = new Map();
    (tab === "explore" ? sessions : mine).forEach((s) => {
      const mon = mondayOf(new Date(`${s.date}T12:00:00`));
      const key = iso(mon);
      if (!map.has(key)) {
        const end = addDays(mon, 6);
        map.set(key, {
          value: key,
          label: `${mon.toLocaleDateString(locale, { day: "numeric", month: "short" })} – ${end.toLocaleDateString(locale, { day: "numeric", month: "short" })}`,
          count: 0,
        });
      }
      map.get(key).count += 1;
    });
    return [...map.values()].sort((a, b) => a.value.localeCompare(b.value));
  }, [sessions, mine, tab, locale]);

  const byDate = useMemo(() => {
    const map = new Map();
    list.forEach((s) => { if (!map.has(s.date)) map.set(s.date, []); map.get(s.date).push(s); });
    return map;
  }, [list]);

  const step = (dir) => {
    setAnchor((a) => (view === "week"
      ? addDays(a, dir * 7)
      : new Date(a.getFullYear(), a.getMonth() + dir, 1)));
  };

  const TypeChip = ({ type }) => (
    <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${liveStyle(type).chip}`}>
      {t(`live_type_${type}`) === `live_type_${type}` ? t("live_type_other") : t(`live_type_${type}`)}
    </span>
  );

  const LevelChip = ({ levels }) => (
    <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full border border-slate-300 bg-white text-slate-700">
      {(levels || []).length ? levels.join(" · ") : t("live_all_levels")}
    </span>
  );

  const ActionButton = ({ s }) => {
    // Sesión terminada: se queda como historial, sin acceso. Los materiales sí.
    if (s.is_past) {
      return (
        <div className="text-xs text-slate-500" data-testid={`live-past-note-${s.id}`}>
          {t("live_past_no_access")}
        </div>
      );
    }
    if (s.my_booking_status === "booked") {
      return (
        <div className="flex flex-col gap-2 items-stretch">
          {s.meet_link && (
            <a href={s.meet_link} target="_blank" rel="noreferrer" data-testid={`live-join-${s.id}`}
              className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 bg-brand-blue text-white hover:bg-[#0F4B99] transition-colors min-h-[44px] w-full sm:w-auto">
              <Video className="h-4 w-4" /> {t("live_join")}
            </a>
          )}
          <AddToCalendarMenu session={s} className="w-full sm:w-auto" />
          <button data-testid={`unbook-live-${s.id}`} onClick={() => unbook(s.id)}
            className="text-xs text-slate-500 hover:text-red-600 transition-colors">
            {t("live_cancel_reservation")}
          </button>
        </div>
      );
    }
    if (s.my_booking_status === "waitlisted") {
      return (
        <div className="flex flex-col gap-2 items-stretch">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("live_waitlist")}
          </span>
          <button data-testid={`unbook-live-${s.id}`} onClick={() => unbook(s.id)}
            className="text-xs text-slate-500 hover:text-red-600 transition-colors">
            {t("live_cancel_reservation")}
          </button>
        </div>
      );
    }
    return (
      <Button
        data-testid={`book-live-${s.id}`}
        onClick={() => book(s.id)}
        className="rounded-full bg-brand hover:brightness-95 text-white px-5 w-full sm:w-auto min-h-[44px]"
      >
        {s.seats_left > 0 ? t("live_reserve") : t("live_join_waitlist")}
      </Button>
    );
  };

  const Materials = ({ s }) => {
    const items = s.materials || [];
    if (!items.length && s.materials_locked) {
      return (
        <p className="mt-3 text-xs text-slate-500 italic" data-testid={`materials-locked-${s.id}`}>{t("live_materials_locked")}</p>
      );
    }
    if (!items.length) return null;
    return (
      <div className="mt-3" data-testid={`materials-${s.id}`}>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{t("live_session_materials")}</div>
        <div className="flex flex-wrap gap-2">
          {items.map((m) => {
            const isRes = m.type === "resource";
            const resUrl = m.url || `${API_BASE}/api/resources/${m.resource_id}/file`;
            const viewUrl = isRes ? resUrl : m.type === "link" ? m.url : `${API_BASE}/api/files/${m.file_id}/view`;
            const dlUrl = isRes ? resUrl : m.type === "link" ? m.url : `${API_BASE}/api/files/${m.file_id}/download`;
            if (m.type === "image") {
              return (
                <div key={m.id} className="w-28">
                  <a href={viewUrl} target="_blank" rel="noreferrer" data-testid={`material-open-${m.id}`}>
                    <img src={viewUrl} alt={m.title}
                      className="h-20 w-28 object-cover rounded-lg border border-slate-200 hover:border-[#004aad] transition-colors" />
                  </a>
                  <a href={dlUrl} data-testid={`material-download-${m.id}`}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#004aad] transition-colors">
                    <Download className="h-3 w-3" /> {t("live_download")}
                  </a>
                </div>
              );
            }
            return (
              <a key={m.id} href={viewUrl} target="_blank" rel="noreferrer" data-testid={`material-open-${m.id}`}
                className="inline-flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-[#004aad] transition-colors max-w-[16rem]">
                {isRes ? <BookOpen className="h-4 w-4 text-brand shrink-0" />
                  : m.type === "video" ? <Film className="h-4 w-4 text-slate-500 shrink-0" />
                  : m.type === "document" ? <FileText className="h-4 w-4 text-rose-500 shrink-0" />
                  : <LinkIcon className="h-4 w-4 text-slate-500 shrink-0" />}
                <span className="truncate">{m.title}</span>
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const first = mondayOf(range.from);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(first, i);
      if (i >= 35 && d > range.to) break;
      cells.push(d);
    }
    return cells;
  }, [view, range]);

  const weekdayNames = useMemo(() => {
    const base = mondayOf(crToday());
    return Array.from({ length: 7 }, (_, i) => addDays(base, i).toLocaleDateString(locale, { weekday: "short" }));
  }, [locale]);

  const todayISO = crTodayISO();

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-blue" data-testid="live-classes-header">
          WSS Live
        </h1>
        <p className="text-base text-slate-500 mt-1">{t("live_tagline")}</p>
        {monthLeft && (monthLeft.upcoming > 0 || monthLeft.total > 0) && (
          <div className="inline-flex items-center gap-2 mt-3 rounded-full bg-orange-50 border border-orange-200 px-3 py-1"
               data-testid="live-month-counter">
            <span className="font-display text-base font-bold text-brand">{monthLeft.upcoming}</span>
            <span className="text-xs text-orange-900/80">
              {t("live_month_summary", { upcoming: monthLeft.upcoming, remaining: monthLeft.remaining, total: monthLeft.total })}
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-white/85 backdrop-blur-md border-b border-slate-200 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-slate-100 p-1">
            {[["explore", t("live_explore")], ["mine", `${t("live_my_reservations")}${mine.length ? ` (${mine.length})` : ""}`]].map(([k, label]) => (
              <button key={k} data-testid={`live-tab-${k}`} onClick={() => setTab(k)}
                className={`text-sm px-4 py-1.5 rounded-full transition-colors ${tab === k ? "bg-white text-brand-blue shadow-sm font-semibold" : "text-slate-500 hover:text-slate-700"}`}>
                {label}
              </button>
            ))}
          </div>

          {tab === "explore" && (
            <>
              {!isMobile && (
                <div className="inline-flex rounded-full bg-slate-100 p-1">
                  {[["all", List, t("live_view_all")], ["week", Rows3, t("live_view_week")], ["month", LayoutGrid, t("live_view_month")]].map(([k, Icon, label]) => (
                    <button key={k} data-testid={`view-toggle-${k}`} onClick={() => setView(k)}
                      className={`inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full transition-colors ${view === k ? "bg-white text-brand-blue shadow-sm font-semibold" : "text-slate-500 hover:text-slate-700"}`}>
                      <Icon className="h-4 w-4" /> {label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 ml-auto w-full sm:w-auto">
                {view === "all" && weekOptions.length > 0 && (
                  <Select value={weekFilter} onValueChange={setWeekFilter}>
                    <SelectTrigger className="h-11 sm:h-9 flex-1 sm:w-48" data-testid="live-week-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("live_all_weeks")}</SelectItem>
                      {weekOptions.map((w) => (
                        <SelectItem key={w.value} value={w.value}>{w.label} · {w.count}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="h-11 sm:h-9 flex-1 sm:w-32" data-testid="live-level-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("live_all_levels")}</SelectItem>
                    {["A1", "A2", "B1", "B2", "C1"].map((lv) => <SelectItem key={lv} value={lv}>{lv}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-11 sm:h-9 flex-1 sm:w-48" data-testid="live-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("live_all_activities")}</SelectItem>
                    {TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`live_type_${ty}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {tab === "explore" && (
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {view !== "all" && (
              <>
                <Button variant="outline" size="sm" className="rounded-full h-8" data-testid="live-today-button"
                  onClick={() => setAnchor(new Date())}>
                  {t("today")}
                </Button>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="live-prev-button" onClick={() => step(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-semibold text-slate-900 capitalize min-w-[11rem] text-center" data-testid="live-range-label">
                    {rangeLabel}
                  </span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="live-next-button" onClick={() => step(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
            {view === "all" && (
              <span className="text-sm font-semibold text-slate-900" data-testid="live-range-label">
                {weekFilter === "all"
                  ? `${t("live_view_all")} · ${list.length}`
                  : (weekOptions.find((w) => w.value === weekFilter)?.label || "")}
              </span>
            )}
            <span className="text-xs text-slate-400 inline-flex items-center gap-1.5 ml-auto">
              <Globe2 className="h-3.5 w-3.5" /> {t("live_timezone")}
            </span>
          </div>
        )}
      </div>

      {tab === "explore" && (
        <p className="text-sm text-slate-500 mb-5" data-testid="live-my-level">
          {t("live_open_to_all")}{myLevel ? ` · ${t("spanish_level")}: ${myLevel}` : ""}
        </p>
      )}

      {/* Empty state */}
      {tab === "mine" && (
        <div className="mb-6"><CalendarSyncCard compact={mine.length === 0} /></div>
      )}

      {!loading && list.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl min-h-[300px] flex flex-col items-center justify-center gap-3 text-center px-6"
          data-testid="live-classes-empty">
          <CalendarX2 className="h-8 w-8 text-slate-300" />
          <p className="text-slate-500">{tab === "explore" ? t("live_no_sessions") : t("live_no_reservations")}</p>
          {tab === "explore" && view !== "all" && (
            <Button variant="outline" className="rounded-full" data-testid="live-next-period-button" onClick={() => step(1)}>
              {view === "week" ? t("live_see_next_week") : t("live_see_next_month")}
            </Button>
          )}
          {tab === "explore" && view === "all" && weekFilter !== "all" && (
            <Button variant="outline" className="rounded-full" data-testid="live-clear-week-filter"
              onClick={() => setWeekFilter("all")}>
              {t("live_all_weeks")}
            </Button>
          )}
        </div>
      )}

      {/* All / week / list view */}
      {(view === "all" || view === "week" || tab === "mine") && list.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="live-week-grid">
          {[...list]
            .sort((a, b) => {
              // Lo que viene primero; el historial al final, de lo más reciente.
              if (!!a.is_past !== !!b.is_past) return a.is_past ? 1 : -1;
              const cmp = (a.date + a.start_time).localeCompare(b.date + b.start_time);
              return a.is_past ? -cmp : cmp;
            })
            .map((s) => (
              <LiveSessionCard
                key={s.id}
                session={s}
                locale={locale}
                onOpen={() => setDetail(s)}
                actions={<ActionButton s={s} />}
                badges={
                  <>
                    {s.is_past && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-700 text-white"
                            data-testid={`live-completed-badge-${s.id}`}>
                        {t("live_status_completed")}
                      </span>
                    )}
                    {!s.is_past && s.date === todayISO && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand text-white">
                        {t("today")}
                      </span>
                    )}
                    {s.my_booking_status === "booked" && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500 text-white"
                            data-testid={`live-card-booked-${s.id}`}>
                        {t("live_confirmed")}
                      </span>
                    )}
                  </>
                }
              />
            ))}
        </div>
      )}

      {/* Month view */}
      {view === "month" && tab === "explore" && list.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="live-month-grid">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {weekdayNames.map((w) => (
              <div key={w} className="px-2 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 text-center capitalize">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d) => {
              const key = iso(d);
              const inMonth = d.getMonth() === range.from.getMonth();
              const items = byDate.get(key) || [];
              return (
                <div key={key}
                  data-testid={`live-month-cell-${key}`}
                  className={`min-h-[120px] p-2 border-r border-b border-slate-100 transition-colors ${inMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/60"}`}>
                  <div className={`text-xs mb-1.5 flex items-center gap-1.5 ${inMonth ? "text-slate-500" : "text-slate-300"}`}>
                    <span className={key === todayISO ? "h-5 w-5 rounded-full bg-brand text-white inline-flex items-center justify-center font-semibold" : ""}>
                      {d.getDate()}
                    </span>
                  </div>
                  {items.map((s) => {
                    const st = liveStyle(s.activity_type);
                    return (
                      <button key={s.id} onClick={() => setDetail(s)} data-testid={`live-month-pill-${s.id}`}
                        title={`${hora12(s.start_time, lang)} · ${s.title}${s.is_past ? ` · ${t("live_status_completed")}` : ""}`}
                        className={`w-full text-left border rounded-md px-1.5 py-1 mb-1 transition-colors ${
                          s.is_past ? "border-slate-200 bg-slate-100 text-slate-400" : st.pill}`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${s.is_past ? "bg-slate-300" : st.dot}`} />
                          <span className="text-[10px] font-mono opacity-80">{hora12(s.start_time, lang)}</span>
                          {s.my_booking_status === "booked" && <span className="text-[9px] text-emerald-600 font-bold">✓</span>}
                        </div>
                        <div className="text-[11px] font-medium truncate leading-tight">{s.title}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Month detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg rounded-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="live-detail-dialog">
              <div className="flex flex-wrap gap-2">
                <TypeChip type={detail.activity_type} />
                <LevelChip levels={detail.levels} />
              </div>
              <div className="text-sm text-slate-600 space-y-1.5">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-slate-400" />
                  <span className="capitalize">
                    {new Date(`${detail.date}T12:00:00`).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}{/* tz-ok: mediodía fijo, el día no se corre */}
                  </span>
                </div>
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {hora12(detail.start_time, lang)} – {hora12(detail.end_time, lang)}</div>
                <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> {detail.teacher_name}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /> {detail.booked_count}/{detail.capacity} · {t("live_seats_left")}: {detail.seats_left}</div>
              </div>
              {detail.description && <p className="text-sm text-slate-600">{detail.description}</p>}
              <Materials s={detail} />
              <div className="pt-1"><ActionButton s={detail} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
